// Derives an ICAO aircraft type designator from a Planespotters photo URL.
//
// Planespotters photo links carry the type in the URL slug, after the
// registration and the operator name:
//
//   https://www.planespotters.net/photo/1943493/ei-ega-ryanair-boeing-737-8as-wl
//                                               |reg | operator|    type      |
//   https://www.planespotters.net/photo/1955543/g-vdot-virgin-atlantic-airbus-a350-1041
//   https://www.planespotters.net/photo/1629593/ei-hab-fedex-feeder-atr-72-600f-72-212a
//
// The operator name is variable-length, so the slug is split at the LAST
// manufacturer name it contains — "last" so that operators whose own name is a
// manufacturer (Airbus Transport International, The Boeing Company) resolve to
// the aircraft rather than to the operator. Everything from there on is matched
// against a table of type rules.
//
// The parser is deliberately conservative: when the model cannot be matched to
// a designator it returns null rather than guessing, because the result is
// offered to admins as a one-click write to the aircraft table.

// Manufacturer names as they appear in slugs, tokenised on '-'. Multi-token
// entries win over shorter ones starting at the same position, so
// "mcdonnell-douglas" is one manufacturer and not "douglas" preceded by noise.
const MANUFACTURERS = [
  ['de', 'havilland', 'canada'],
  ['de', 'havilland'],
  ['mcdonnell', 'douglas'],
  ['british', 'aerospace'],
  ['airbus', 'helicopters'],
  ['airbus', 'industrie'],
  ['britten', 'norman'],
  ['air', 'tractor'],
  ['bae', 'systems'],
  ['agusta', 'westland'],
  ['agustawestland'],
  ['aerospatiale'],
  ['antonov'],
  ['atr'],
  ['avro'],
  ['airbus'],
  ['bae'],
  ['beechcraft'],
  ['beech'],
  ['bell'],
  ['boeing'],
  ['bombardier'],
  ['canadair'],
  ['cessna'],
  ['cirrus'],
  ['comac'],
  ['daher'],
  ['dassault'],
  ['diamond'],
  ['dornier'],
  ['douglas'],
  ['embraer'],
  ['eurocopter'],
  ['extra'],
  ['fokker'],
  ['grumman'],
  ['gulfstream'],
  ['hawker'],
  ['honda'],
  ['ilyushin'],
  ['learjet'],
  ['leonardo'],
  ['let'],
  ['lockheed'],
  ['mbb'],
  ['mooney'],
  ['partenavia'],
  ['piaggio'],
  ['pilatus'],
  ['viking'], // Viking Air: DHC-6-400 Twin Otter production since 2010
  ['pipistrel'],
  ['elixir'],
  ['sling'],
  ['piper'],
  ['quest'],
  ['robin'],
  ['robinson'],
  ['saab'],
  ['sikorsky'],
  ['socata'],
  ['sukhoi'],
  ['tecnam'],
  ['tupolev'],
  ['vulcanair'],
];

// [pattern tested against the manufacturer-onwards slug, ICAO designator,
//  manufacturer, model]. First match wins, so more specific variants (neo,
//  MAX, ER, E2) must be listed before the base families they derive from.
const TYPE_RULES = [
  // Airbus
  [/^airbus-a300.*\dst$/, 'A3ST', 'Airbus', 'A300-600ST Beluga'],
  [/^airbus-a330-74/, 'A337', 'Airbus', 'A330-700 Beluga XL'],
  [/^airbus-a220-1/, 'BCS1', 'Airbus', 'A220-100'],
  [/^airbus-a220-3/, 'BCS3', 'Airbus', 'A220-300'],
  [/^airbus-a300-?b?[1-4]?-?6/, 'A306', 'Airbus', 'A300-600'],
  [/^airbus-a300/, 'A30B', 'Airbus', 'A300B4'],
  [/^airbus-a310/, 'A310', 'Airbus', 'A310'],
  [/^airbus-a318/, 'A318', 'Airbus', 'A318'],
  [/^airbus-a319(?:-\d{3}nx?|neo)/, 'A19N', 'Airbus', 'A319neo'],
  [/^airbus-a319/, 'A319', 'Airbus', 'A319'],
  [/^airbus-a320(?:-\d{3}nx?|neo)/, 'A20N', 'Airbus', 'A320neo'],
  [/^airbus-a320/, 'A320', 'Airbus', 'A320'],
  [/^airbus-a321(?:-\d{3}nx?|neo)/, 'A21N', 'Airbus', 'A321neo'],
  [/^airbus-a321/, 'A321', 'Airbus', 'A321'],
  [/^airbus-a330-2/, 'A332', 'Airbus', 'A330-200'],
  [/^airbus-a330-3/, 'A333', 'Airbus', 'A330-300'],
  [/^airbus-a330-8/, 'A338', 'Airbus', 'A330-800neo'],
  [/^airbus-a330-9/, 'A339', 'Airbus', 'A330-900neo'],
  [/^airbus-a340-2/, 'A342', 'Airbus', 'A340-200'],
  [/^airbus-a340-3/, 'A343', 'Airbus', 'A340-300'],
  [/^airbus-a340-5/, 'A345', 'Airbus', 'A340-500'],
  [/^airbus-a340-6/, 'A346', 'Airbus', 'A340-600'],
  [/^airbus-a350-10/, 'A35K', 'Airbus', 'A350-1000'],
  [/^airbus-a350-9/, 'A359', 'Airbus', 'A350-900'],
  [/^airbus-a380/, 'A388', 'Airbus', 'A380-800'],
  [/^airbus-a400m/, 'A400', 'Airbus', 'A400M Atlas'],

  // Airbus Helicopters / Eurocopter / Aerospatiale
  [/^(?:airbus-helicopters|eurocopter)-(?:ec-?120|h120)/, 'EC20', 'Airbus Helicopters', 'EC120 Colibri'],
  [/^(?:airbus-helicopters|eurocopter)-(?:ec-?130|h130)/, 'EC30', 'Airbus Helicopters', 'H130'],
  [/^(?:airbus-helicopters|eurocopter)-(?:ec-?135|h135)/, 'EC35', 'Airbus Helicopters', 'H135'],
  [/^(?:airbus-helicopters|eurocopter)-(?:ec-?145|h145|bk-?117)/, 'EC45', 'Airbus Helicopters', 'H145'],
  [/^(?:airbus-helicopters|eurocopter)-(?:ec-?155|h155)/, 'EC55', 'Airbus Helicopters', 'H155'],
  [/^(?:airbus-helicopters|eurocopter)-(?:ec-?175|h175)/, 'EC75', 'Airbus Helicopters', 'H175'],
  [/^(?:airbus-helicopters|eurocopter)-h160/, 'H160', 'Airbus Helicopters', 'H160'],
  [/^(?:airbus-helicopters|eurocopter|aerospatiale)-as-?355/, 'AS55', 'Airbus Helicopters', 'AS355 Ecureuil 2'],
  [/^(?:airbus-helicopters|eurocopter|aerospatiale)-as-?365/, 'AS65', 'Airbus Helicopters', 'AS365 Dauphin'],
  [/^(?:airbus-helicopters|eurocopter|aerospatiale)-(?:as-?350|h125|ecureuil)/, 'AS50', 'Airbus Helicopters', 'H125'],
  [/^(?:airbus-helicopters|eurocopter|aerospatiale)-(?:sa-?34[12]|gazelle)/, 'GAZL', 'Aerospatiale', 'SA342 Gazelle'],
  [/^mbb-bo-?105/, 'BO05', 'MBB', 'Bo 105'],

  // Boeing
  [/^boeing-717/, 'B712', 'Boeing', '717-200'],
  [/^boeing-727-1/, 'B721', 'Boeing', '727-100'],
  [/^boeing-727/, 'B722', 'Boeing', '727-200'],
  [/^boeing-737-(?:max-)?7(?:-\d+)?-?max/, 'B37M', 'Boeing', '737 MAX 7'],
  [/^boeing-737-(?:max-)?8(?:-\d+)?-?max/, 'B38M', 'Boeing', '737 MAX 8'],
  [/^boeing-737-(?:max-)?9(?:-\d+)?-?max/, 'B39M', 'Boeing', '737 MAX 9'],
  [/^boeing-737-(?:max-)?10(?:-\d+)?-?max/, 'B3XM', 'Boeing', '737 MAX 10'],
  [/^boeing-737-2/, 'B732', 'Boeing', '737-200'],
  [/^boeing-737-3/, 'B733', 'Boeing', '737-300'],
  [/^boeing-737-4/, 'B734', 'Boeing', '737-400'],
  [/^boeing-737-5/, 'B735', 'Boeing', '737-500'],
  [/^boeing-737-6/, 'B736', 'Boeing', '737-600'],
  [/^boeing-737-7/, 'B737', 'Boeing', '737-700'],
  [/^boeing-737-8/, 'B738', 'Boeing', '737-800'],
  [/^boeing-737-9/, 'B739', 'Boeing', '737-900'],
  [/^boeing-747-sp/, 'B74S', 'Boeing', '747SP'],
  [/^boeing-747-1/, 'B741', 'Boeing', '747-100'],
  [/^boeing-747-2/, 'B742', 'Boeing', '747-200'],
  [/^boeing-747-3/, 'B743', 'Boeing', '747-300'],
  [/^boeing-747-4/, 'B744', 'Boeing', '747-400'],
  [/^boeing-747-8/, 'B748', 'Boeing', '747-8'],
  [/^boeing-757-2/, 'B752', 'Boeing', '757-200'],
  [/^boeing-757-3/, 'B753', 'Boeing', '757-300'],
  [/^boeing-767-2/, 'B762', 'Boeing', '767-200'],
  [/^boeing-767-3/, 'B763', 'Boeing', '767-300'],
  [/^boeing-767-4/, 'B764', 'Boeing', '767-400'],
  [/^boeing-777-2\d\dlr/, 'B77L', 'Boeing', '777-200LR'],
  [/^boeing-777-f/, 'B77L', 'Boeing', '777F'],
  [/^boeing-777-2/, 'B772', 'Boeing', '777-200'],
  [/^boeing-777-3\d\der/, 'B77W', 'Boeing', '777-300ER'],
  [/^boeing-777-3/, 'B773', 'Boeing', '777-300'],
  [/^boeing-777-9/, 'B779', 'Boeing', '777-9'],
  [/^boeing-787-10/, 'B78X', 'Boeing', '787-10'],
  [/^boeing-787-8/, 'B788', 'Boeing', '787-8'],
  [/^boeing-787-9/, 'B789', 'Boeing', '787-9'],
  [/^boeing-c-?17|^mcdonnell-douglas-c-?17/, 'C17', 'Boeing', 'C-17 Globemaster III'],
  [/^(?:boeing|mcdonnell-douglas)-md-?11/, 'MD11', 'Boeing', 'MD-11'],
  [/^(?:boeing|mcdonnell-douglas)-md-?8[0-8]/, 'MD82', 'McDonnell Douglas', 'MD-82'],
  [/^(?:boeing|mcdonnell-douglas)-md-?90/, 'MD90', 'McDonnell Douglas', 'MD-90'],
  [/^(?:boeing|mcdonnell-douglas)-dc-?10/, 'DC10', 'McDonnell Douglas', 'DC-10'],
  [/^(?:mcdonnell-)?douglas-dc-?3/, 'DC3', 'Douglas', 'DC-3'],
  [/^(?:mcdonnell-)?douglas-dc-?6/, 'DC6', 'Douglas', 'DC-6'],

  // Embraer — E2 and stretched sub-series before the base families
  [/^embraer-(?:e-?|erj-)?175-e2|^embraer-erj-170-4/, 'E275', 'Embraer', 'E175-E2'],
  [/^embraer-(?:e-?|erj-)?190-e2|^embraer-erj-190-3/, 'E290', 'Embraer', 'E190-E2'],
  [/^embraer-(?:e-?|erj-)?195-e2|^embraer-erj-190-4/, 'E295', 'Embraer', 'E195-E2'],
  [/^embraer-erj-170-2/, 'E175', 'Embraer', 'E175'],
  [/^embraer-erj-190-2/, 'E195', 'Embraer', 'E195'],
  [/^embraer-emb-?110/, 'E110', 'Embraer', 'EMB 110 Bandeirante'],
  [/^embraer-emb-?120/, 'E120', 'Embraer', 'EMB 120 Brasilia'],
  [/^embraer-(?:e-?|erj-)?135/, 'E135', 'Embraer', 'ERJ 135'],
  [/^embraer-(?:e-?|erj-)?140/, 'E140', 'Embraer', 'ERJ 140'],
  [/^embraer-(?:e-?|erj-)?145/, 'E145', 'Embraer', 'ERJ 145'],
  // E-Jets. Planespotters slugs carry the certification name, which does not
  // line up with the marketing name: ERJ 170-200 is the E175, ERJ 190-200 the
  // E195, and the E2s are ERJ 190-300/-400/-500 (E190-E2/E195-E2/E175-E2).
  // Certification and E2 forms first so the loose marketing rules below
  // cannot claim them.
  [/^embraer-(?:.*erj-190-400|e-?195-e2)/, 'E295', 'Embraer', 'E195-E2'],
  [/^embraer-(?:.*erj-190-300|e-?190-e2)/, 'E290', 'Embraer', 'E190-E2'],
  [/^embraer-(?:.*erj-190-500|e-?175-e2)/, 'E275', 'Embraer', 'E175-E2'],
  [/^embraer-(?:.*erj-190-200|(?:e-?|erj-)?195)/, 'E195', 'Embraer', 'E195'],
  [/^embraer-(?:.*erj-190-100|(?:e-?|erj-)?190)/, 'E190', 'Embraer', 'E190'],
  [/^embraer-(?:.*erj-170-200|(?:e-?|erj-)?175)/, 'E175', 'Embraer', 'E175'],
  [/^embraer-(?:.*erj-170-100|(?:e-?|erj-)?170)/, 'E170', 'Embraer', 'E170'],
  [/^embraer-phenom-?100|^embraer-emb-?500/, 'E50P', 'Embraer', 'Phenom 100'],
  [/^embraer-phenom-?300|^embraer-emb-?505/, 'E55P', 'Embraer', 'Phenom 300'],
  [/^embraer-legacy-6/, 'E35L', 'Embraer', 'Legacy 600'],
  [/^embraer-(?:legacy-5|praetor-6)/, 'E550', 'Embraer', 'Legacy 500'],
  [/^embraer-(?:legacy-4|praetor-5)/, 'E545', 'Embraer', 'Praetor 500'],

  // ATR
  [/^atr-42-[34]/, 'AT43', 'ATR', '42-300'],
  [/^atr-42-5/, 'AT45', 'ATR', '42-500'],
  [/^atr-42-6/, 'AT46', 'ATR', '42-600'],
  [/^atr-72-5/, 'AT75', 'ATR', '72-500'],
  [/^atr-72-6/, 'AT76', 'ATR', '72-600'],
  [/^atr-72/, 'AT72', 'ATR', '72-200'],

  // Bombardier / Canadair / De Havilland Canada
  [/^(?:bombardier|canadair).*crj-?1000/, 'CRJX', 'Bombardier', 'CRJ1000'],
  [/^(?:bombardier|canadair).*crj-?100/, 'CRJ1', 'Bombardier', 'CRJ100'],
  [/^(?:bombardier|canadair).*crj-?200/, 'CRJ2', 'Bombardier', 'CRJ200'],
  [/^(?:bombardier|canadair).*crj-?700/, 'CRJ7', 'Bombardier', 'CRJ700'],
  [/^(?:bombardier|canadair).*crj-?900/, 'CRJ9', 'Bombardier', 'CRJ900'],
  [/^(?:bombardier|airbus)-(?:cseries-)?cs-?100/, 'BCS1', 'Airbus', 'A220-100'],
  [/^(?:bombardier|airbus)-(?:cseries-)?cs-?300/, 'BCS3', 'Airbus', 'A220-300'],
  [/^bombardier-global-7500|^bombardier-bd-?700-2a12/, 'GL7T', 'Bombardier', 'Global 7500'],
  [/^bombardier-global|^bombardier-bd-?700/, 'GLEX', 'Bombardier', 'Global Express'],
  [/^(?:bombardier|canadair)-challenger-3|^bombardier-bd-?100/, 'CL30', 'Bombardier', 'Challenger 300'],
  [/^(?:bombardier|canadair)-challenger-8/, 'CRJ2', 'Bombardier', 'Challenger 850'],
  // CL60 is the whole 600 line; only the label (saved as the model) differs.
  [/^(?:bombardier|canadair)-challenger-650/, 'CL60', 'Bombardier', 'Challenger 650'],
  [/^(?:bombardier|canadair)-challenger-605/, 'CL60', 'Bombardier', 'Challenger 605'],
  [/^(?:bombardier|canadair)-challenger-604/, 'CL60', 'Bombardier', 'Challenger 604'],
  [/^(?:bombardier|canadair)-challenger-601/, 'CL60', 'Bombardier', 'Challenger 601'],
  [/^(?:bombardier|canadair)-challenger-600/, 'CL60', 'Bombardier', 'Challenger 600'],
  [/^(?:bombardier|canadair)-(?:challenger-[567]|cl-?600)/, 'CL60', 'Bombardier', 'Challenger 600 series'],
  [/^(?:bombardier-|learjet-).*learjet-?4[05]|^learjet-4[05]/, 'LJ45', 'Bombardier', 'Learjet 45'],
  [/^(?:bombardier-|learjet-).*learjet-?60|^learjet-60/, 'LJ60', 'Bombardier', 'Learjet 60'],
  [/^(?:bombardier-|learjet-).*learjet-?75|^learjet-75/, 'LJ75', 'Bombardier', 'Learjet 75'],
  [/(?:dhc-8-4|dash-8-4|q400)/, 'DH8D', 'De Havilland Canada', 'Dash 8-400'],
  [/(?:dhc-8-3|dash-8-3)/, 'DH8C', 'De Havilland Canada', 'Dash 8-300'],
  [/(?:dhc-8-2|dash-8-2)/, 'DH8B', 'De Havilland Canada', 'Dash 8-200'],
  [/(?:dhc-8-1|dash-8-1)/, 'DH8A', 'De Havilland Canada', 'Dash 8-100'],
  [/^(?:de-havilland-canada|viking)-dhc-?6/, 'DHC6', 'De Havilland Canada', 'DHC-6 Twin Otter'],
  [/^de-havilland-canada-dhc-?2/, 'DHC2', 'De Havilland Canada', 'DHC-2 Beaver'],
  [/^de-havilland-canada-dhc-?7/, 'DHC7', 'De Havilland Canada', 'DHC-7 Dash 7'],
  [/^de-havilland-(?:canada-)?dh-?c?82|tiger-moth/, 'TIGM', 'De Havilland', 'DH.82 Tiger Moth'],

  // Cessna
  [/^cessna-1[45]0|^cessna-152/, 'C152', 'Cessna', '152'],
  [/^cessna-17[26]/, 'C172', 'Cessna', '172 Skyhawk'],
  [/^cessna-18[24]/, 'C182', 'Cessna', '182 Skylane'],
  [/^cessna-20[68]/, 'C208', 'Cessna', '208 Caravan'],
  [/^cessna-210/, 'C210', 'Cessna', '210 Centurion'],
  [/^cessna-4(?:02|04|06)/, 'C402', 'Cessna', '402'],
  [/^cessna-(?:414|421)/, 'C421', 'Cessna', '421 Golden Eagle'],
  [/^cessna-510|citation-mustang/, 'C510', 'Cessna', 'Citation Mustang'],
  [/^cessna-525.*cj[34]|citation-cj[34]/, 'C25B', 'Cessna', 'Citation CJ3'],
  [/^cessna-525|citation-(?:cj[12]|jet)/, 'C25A', 'Cessna', 'Citation CJ2'],
  [/^cessna-550|citation-bravo/, 'C550', 'Cessna', 'Citation II'],
  [/^cessna-560-?xls?|citation-(?:excel|xls)/, 'C56X', 'Cessna', 'Citation XLS'],
  [/^cessna-560|citation-(?:encore|ultra)/, 'C560', 'Cessna', 'Citation V'],
  [/^cessna-680|citation-(?:sovereign|latitude)/, 'C680', 'Cessna', 'Citation Sovereign'],
  [/^cessna-700|citation-longitude/, 'C700', 'Cessna', 'Citation Longitude'],
  [/^cessna-750|citation-x/, 'C750', 'Cessna', 'Citation X'],

  // Business / general aviation
  [/^dassault-falcon-?2000/, 'F2TH', 'Dassault', 'Falcon 2000'],
  [/^dassault-falcon-?900/, 'F900', 'Dassault', 'Falcon 900'],
  [/^dassault-falcon-?7x/, 'FA7X', 'Dassault', 'Falcon 7X'],
  [/^dassault-falcon-?8x/, 'F8X', 'Dassault', 'Falcon 8X'],
  [/^dassault-falcon-?6x/, 'FA6X', 'Dassault', 'Falcon 6X'],
  [/^dassault-falcon-?50/, 'FA50', 'Dassault', 'Falcon 50'],
  [/^dassault-falcon-?20/, 'FA20', 'Dassault', 'Falcon 20'],
  [/^dassault-falcon-?10/, 'FA10', 'Dassault', 'Falcon 10'],
  [/^gulfstream-(?:g-?280|g-?200|galaxy)/, 'GALX', 'Gulfstream', 'G280'],
  [/^gulfstream-(?:g-?4|giv)/, 'GLF4', 'Gulfstream', 'G450'],
  [/^gulfstream-(?:g-?5|gv)/, 'GLF5', 'Gulfstream', 'G550'],
  [/^gulfstream-(?:g-?6|g-?7)/, 'GLF6', 'Gulfstream', 'G650'],
  [/^(?:hawker|british-aerospace|bae)-(?:hs-?125|125|800|850|900)/, 'H25B', 'Hawker', '800'],
  // Hawker Beechcraft (the merged marque, 2007-2013) writes BOTH names into the slug:
  // "oy-jjm-joinjet-hawker-beechcraft-4000". manufacturerOnwards() takes the LAST
  // manufacturer it finds, so these rules see "beechcraft-..." with the hawker- already
  // stripped — hence the optional prefix rather than a required one.
  //
  // The 4000 (Horizon) is a clean-sheet composite airframe: neither a King Air nor an
  // 800, and it had no rule at all, so every Hawker 4000 photo returned null.
  [/^(?:hawker-)?(?:hawker|beech(?:craft)?)-4000/, 'HA4T', 'Hawker Beechcraft', '4000'],
  // 750 / 800XP / 850XP / 900XP are all 800-series airframes and all H25B. This MUST stay
  // ahead of the King Air 90 rule below: that rule's "90" happily matches the "90" of
  // "900xp", so "hawker-beechcraft-900xp" was being reported as a King Air 90 (BE9L) —
  // a confident wrong answer, which is worse than the null this parser promises.
  // The 750 carries no XP suffix; the 800/850/900 always do.
  [/^(?:hawker-)?beech(?:craft)?-(?:750|(?:800|850|900)xp)/, 'H25B', 'Hawker', '800XP'],
  [/^honda-ha-?420|hondajet/, 'HDJT', 'Honda', 'HA-420 HondaJet'],
  [/^pilatus-pc-?6/, 'PC6T', 'Pilatus', 'PC-6 Porter'],
  [/^pilatus-pc-?12/, 'PC12', 'Pilatus', 'PC-12'],
  [/^pilatus-pc-?21/, 'PC21', 'Pilatus', 'PC-21'],
  [/^pilatus-pc-?24/, 'PC24', 'Pilatus', 'PC-24'],
  [/^piaggio-p-?180/, 'P180', 'Piaggio', 'P.180 Avanti'],
  [/^(?:daher|socata)-.*tbm|^(?:daher|socata)-tbm/, 'TBM9', 'Daher', 'TBM 900'],
  [/^beech(?:craft)?-1900/, 'B190', 'Beechcraft', '1900'],
  [/^beech(?:craft)?-(?:b?300|350)|king-air-350/, 'B350', 'Beechcraft', 'King Air 350'],
  [/^beech(?:craft)?-b?200|king-air-200|200-king-air/, 'BE20', 'Beechcraft', 'King Air 200'],
  // (?!\d) so "90" cannot swallow the leading 90 of a 900XP — see the Hawker rules above.
  [/^beech(?:craft)?-c?90(?!\d)|king-air-90(?!\d)|90-king-air/, 'BE9L', 'Beechcraft', 'King Air 90'],
  [/^beech(?:craft)?-(?:58|baron)/, 'BE58', 'Beechcraft', 'Baron 58'],
  [/^beech(?:craft)?-(?:a?36|bonanza)/, 'BE36', 'Beechcraft', 'Bonanza 36'],
  [/^cirrus-sr-?20/, 'SR20', 'Cirrus', 'SR20'],
  [/^cirrus-sr-?22/, 'SR22', 'Cirrus', 'SR22'],
  [/^cirrus-sf-?50|vision-jet/, 'SF50', 'Cirrus', 'SF50 Vision Jet'],
  [/^diamond-d[av]-?20|^diamond-.*katana/, 'DV20', 'Diamond', 'DA20 Katana'],
  [/^diamond-da-?40/, 'DA40', 'Diamond', 'DA40'],
  [/^diamond-da-?42/, 'DA42', 'Diamond', 'DA42 Twin Star'],
  [/^diamond-da-?62/, 'DA62', 'Diamond', 'DA62'],
  [/^piper-pa-?18/, 'PA18', 'Piper', 'PA-18 Super Cub'],
  [/^piper-pa-?23/, 'PA23', 'Piper', 'PA-23 Aztec'],
  [/^piper-pa-?28r/, 'P28R', 'Piper', 'PA-28R Arrow'],
  [/^piper-pa-?28/, 'P28A', 'Piper', 'PA-28 Cherokee'],
  [/^piper-pa-?31/, 'PA31', 'Piper', 'PA-31 Navajo'],
  [/^piper-pa-?32/, 'P32R', 'Piper', 'PA-32R Saratoga'],
  [/^piper-pa-?34/, 'PA34', 'Piper', 'PA-34 Seneca'],
  [/^piper-pa-?44/, 'PA44', 'Piper', 'PA-44 Seminole'],
  [/^piper-(?:pa-?46|m[56]00)|meridian/, 'PA46', 'Piper', 'PA-46 Malibu'],
  [/^robin-dr-?400/, 'DR40', 'Robin', 'DR400'],
  [/^mooney-m-?20/, 'M20P', 'Mooney', 'M20'],
  [/^tecnam-p-?2006/, 'P06T', 'Tecnam', 'P2006T'],
  // Elixir Aircraft's only model; the slug carries no separate maker token.
  [/^elixir/, 'LXR', 'Elixir Aircraft', 'Elixir'],
  // Sling TSi is the four-seat Sling; high-wing and two-seat differ.
  [/^sling-4-high/, 'SLH4', 'Sling Aircraft', 'Sling 4 High Wing'],
  [/^sling-(?:4|tsi)/, 'SLG4', 'Sling Aircraft', 'Sling 4 TSi'],
  [/^sling-2/, 'SLG2', 'Sling Aircraft', 'Sling 2'],
  [/^pipistrel-velis/, 'PIVE', 'Pipistrel', 'Velis Electro'],
  // The Explorer is the Virus SW airframe and shares its designator.
  [/^pipistrel-explorer/, 'PIVI', 'Pipistrel', 'Explorer'],
  [/^pipistrel-virus/, 'PIVI', 'Pipistrel', 'Virus'],
  [/^pipistrel-alpha-electro/, 'PIAE', 'Pipistrel', 'Alpha Electro'],
  [/^pipistrel-alpha/, 'PIAT', 'Pipistrel', 'Alpha Trainer'],
  [/^pipistrel-panthera/, 'PIPA', 'Pipistrel', 'Panthera'],
  [/^pipistrel-sinus/, 'PISI', 'Pipistrel', 'Sinus'],
  [/^extra-ea-?300|^extra-300/, 'E300', 'Extra', 'EA-300'],
  [/^(?:vulcanair|partenavia)-p-?68/, 'P68', 'Vulcanair', 'P.68'],
  [/^quest-kodiak/, 'KODI', 'Quest', 'Kodiak 100'],
  [/^britten-norman-bn-?2t/, 'BN2T', 'Britten-Norman', 'BN-2T Turbine Islander'],
  [/^britten-norman-bn-?2|islander/, 'BN2P', 'Britten-Norman', 'BN-2 Islander'],
  [/^air-tractor-at-?8/, 'AT8T', 'Air Tractor', 'AT-802'],

  // Regional turboprops and older jets
  [/^saab-340/, 'SF34', 'Saab', '340'],
  [/^saab-2000/, 'SB20', 'Saab', '2000'],
  [/^fokker-50/, 'F50', 'Fokker', '50'],
  [/^fokker-70/, 'F70', 'Fokker', '70'],
  [/^fokker-100/, 'F100', 'Fokker', '100'],
  // Planespotters writes the marque in: "dornier-do-228-212", "dornier-do-328-110".
  [/^dornier-(?:do-?)?328-?j/, 'J328', 'Dornier', '328JET'],
  [/^dornier-(?:do-?)?328/, 'D328', 'Dornier', '328'],
  [/^dornier-(?:do-?)?228/, 'D228', 'Dornier', '228'],
  [/^(?:british-aerospace|bae|avro).*rj-?85/, 'RJ85', 'Avro', 'RJ85'],
  [/^(?:british-aerospace|bae|avro).*rj-?100/, 'RJ1H', 'Avro', 'RJ100'],
  [/^(?:british-aerospace|bae|avro).*rj-?70/, 'RJ70', 'Avro', 'RJ70'],
  [/^(?:british-aerospace|bae|avro).*146-?300/, 'B463', 'British Aerospace', '146-300'],
  [/^(?:british-aerospace|bae|avro).*146-?200/, 'B462', 'British Aerospace', '146-200'],
  [/^(?:british-aerospace|bae|avro).*146/, 'B461', 'British Aerospace', '146-100'],
  [/^(?:british-aerospace|bae).*(?:jetstream-?4|j-?41)/, 'JS41', 'British Aerospace', 'Jetstream 41'],
  [/^(?:british-aerospace|bae).*(?:jetstream-?3|j-?31)/, 'JS32', 'British Aerospace', 'Jetstream 32'],
  [/^(?:british-aerospace|bae)-atp/, 'ATP', 'British Aerospace', 'ATP'],
  [/^let-l-?410/, 'L410', 'LET', 'L-410 Turbolet'],
  [/^sukhoi-.*(?:superjet|ssj)/, 'SU95', 'Sukhoi', 'Superjet 100'],
  [/^comac-c-?919/, 'C919', 'COMAC', 'C919'],
  [/^comac-arj-?21/, 'AJ21', 'COMAC', 'ARJ21'],
  [/^antonov-an-?124/, 'A124', 'Antonov', 'An-124 Ruslan'],
  [/^antonov-an-?225/, 'A225', 'Antonov', 'An-225 Mriya'],
  [/^antonov-an-?26/, 'AN26', 'Antonov', 'An-26'],
  [/^antonov-an-?12/, 'AN12', 'Antonov', 'An-12'],
  [/^antonov-an-?7[24]/, 'AN72', 'Antonov', 'An-72'],
  [/^antonov-an-?2/, 'AN2', 'Antonov', 'An-2'],
  [/^ilyushin-il-?76/, 'IL76', 'Ilyushin', 'Il-76'],
  [/^ilyushin-il-?96/, 'IL96', 'Ilyushin', 'Il-96'],
  [/^tupolev-tu-?2[01]4/, 'T204', 'Tupolev', 'Tu-204'],
  [/^lockheed-.*(?:c-?130|hercules)/, 'C130', 'Lockheed', 'C-130 Hercules'],
  [/^lockheed-.*(?:l-?1011|tristar)/, 'L101', 'Lockheed', 'L-1011 TriStar'],
  [/^lockheed-.*c-?5/, 'C5M', 'Lockheed', 'C-5 Galaxy'],

  // Helicopters
  [/^(?:agustawestland|agusta-westland|leonardo).*aw-?109/, 'A109', 'Leonardo', 'AW109'],
  [/^(?:agustawestland|agusta-westland|leonardo).*aw-?119/, 'A119', 'Leonardo', 'AW119 Koala'],
  [/^(?:agustawestland|agusta-westland|leonardo).*aw-?139/, 'A139', 'Leonardo', 'AW139'],
  [/^(?:agustawestland|agusta-westland|leonardo).*aw-?169/, 'A169', 'Leonardo', 'AW169'],
  [/^(?:agustawestland|agusta-westland|leonardo).*aw-?189/, 'A189', 'Leonardo', 'AW189'],
  [/^bell-206|jet-ranger-iii/, 'B06', 'Bell', '206 JetRanger'],
  [/^bell-407/, 'B407', 'Bell', '407'],
  [/^bell-412/, 'B412', 'Bell', '412'],
  [/^bell-429/, 'B429', 'Bell', '429'],
  [/^bell-505/, 'B505', 'Bell', '505 Jet Ranger X'],
  [/^robinson-r-?22/, 'R22', 'Robinson', 'R22'],
  [/^robinson-r-?44/, 'R44', 'Robinson', 'R44'],
  [/^robinson-r-?66/, 'R66', 'Robinson', 'R66'],
  [/^sikorsky-s-?76/, 'S76', 'Sikorsky', 'S-76'],
  [/^sikorsky-s-?92/, 'S92', 'Sikorsky', 'S-92'],
  [/^sikorsky-.*(?:uh-?60|s-?70|black-?hawk)/, 'H60', 'Sikorsky', 'UH-60 Black Hawk'],
];

// Manufacturer entries indexed by first token, longest first, so a lookup at a
// given token position only has to try the handful of entries that can match.
const MANUFACTURERS_BY_HEAD = MANUFACTURERS.reduce((acc, tokens) => {
  const head = tokens[0];
  (acc[head] = acc[head] || []).push(tokens);
  return acc;
}, {});
Object.values(MANUFACTURERS_BY_HEAD).forEach(list => list.sort((a, b) => b.length - a.length));

/**
 * Reduces a Planespotters photo URL to its slug tokens.
 * Returns null for anything that isn't a usable slug.
 */
function slugTokens(url) {
  if (!url || typeof url !== 'string') return null;

  // Strip query/hash, take the last path segment, drop any file extension.
  const path = url.split(/[?#]/)[0];
  const segment = path.split('/').filter(Boolean).pop();
  if (!segment) return null;

  const slug = segment.replace(/\.[a-z0-9]{2,4}$/i, '').toLowerCase();
  const tokens = slug.split('-').filter(Boolean);
  return tokens.length ? tokens : null;
}

/**
 * Returns the slug from the last manufacturer name onwards, e.g.
 * "ei-ega-ryanair-boeing-737-8as-wl" -> "boeing-737-8as-wl".
 * Returns null when the slug names no manufacturer we know.
 */
function manufacturerOnwards(tokens) {
  let start = -1;
  for (let i = 0; i < tokens.length;) {
    const candidates = MANUFACTURERS_BY_HEAD[tokens[i]];
    const match = candidates && candidates.find(
      entry => entry.every((t, k) => tokens[i + k] === t)
    );
    if (match) {
      start = i;
      i += match.length; // skip past it, so "douglas" in "mcdonnell-douglas" isn't a second hit
    } else {
      i += 1;
    }
  }
  return start === -1 ? null : tokens.slice(start).join('-');
}

/**
 * Parses an aircraft photo URL and returns the aircraft type it names.
 *
 * @param {string} url Planespotters photo link (photo.imageUrl)
 * @returns {{typeCode: string, manufacturer: string, model: string, label: string, slug: string}|null}
 *          null when the URL names no manufacturer/model we recognise.
 */
export function parseAircraftTypeFromPhotoUrl(url) {
  const tokens = slugTokens(url);
  if (!tokens) return null;

  const slug = manufacturerOnwards(tokens);
  if (!slug) return null;

  const rule = TYPE_RULES.find(([re]) => re.test(slug));
  if (!rule) return null;

  const [, typeCode, manufacturer, model] = rule;
  return {
    typeCode,
    manufacturer,
    model,
    label: `${manufacturer} ${model}`,
    slug
  };
}

// Designators that are the same airframe for the purpose of "does the photo
// show a different aircraft?". The photo parser cannot always tell sub-variants
// apart (any Global is GLEX, any Baron BE58, a Latitude C680), and registries
// use several codes for one family (E175 / E75L / E75S). None of those is a
// recycled-registration mismatch, so they must not raise a conflict. Genuinely
// different variants the parser DOES distinguish (A320 vs A20N, 737-800 vs
// MAX 8) are deliberately not grouped: the photo is then a real correction.
const SAME_FAMILY = [
  ['E170', 'E175', 'E75L', 'E75S'],
  ['E190', 'E195'],
  ['E135', 'E140', 'E145'],
  ['B461', 'B462', 'B463', 'RJ70', 'RJ85', 'RJ1H'],
  ['MD81', 'MD82', 'MD83', 'MD87', 'MD88'],
  ['AT72', 'AT75', 'AT76'],
  ['AT43', 'AT45', 'AT46'],
  ['DH8A', 'DH8B', 'DH8C'],
  ['GLEX', 'GL5T', 'GL6T'],
  ['GALX', 'G280'],
  ['GLF4', 'GLF5', 'GLF6', 'GA5C', 'GA6C', 'GA7C', 'GA8C'],
  ['CL30', 'CL35'],
  ['CL60', 'CL64', 'CL65'],
  ['C25A', 'C25B', 'C25C', 'C25M', 'C525'],
  ['C550', 'C551', 'C560', 'C56X'],
  ['C680', 'C68A'],
  ['C700', 'C750'],
  ['LJ35', 'LJ36', 'LJ40', 'LJ45'],
  ['LJ70', 'LJ75'],
  ['H25A', 'H25B', 'H25C'],
  ['F2TH', 'F900', 'FA7X', 'F8X', 'FA8X', 'FA6X'],
  ['BE20', 'B350', 'BE30', 'BE9L', 'BE9T'],
  ['BE55', 'BE58'],
  ['BE33', 'BE35', 'BE36'],
  ['PA46', 'P46T'],
  ['P31', 'PA31'],
  ['P28A', 'P28B', 'P28T', 'P28R'],
  ['P32R', 'PA32', 'P32T'],
  ['C150', 'C152'],
  ['C172', 'C72R', 'C175', 'C177', 'C77R'],
  ['C180', 'C182', 'C82R', 'C82T', 'C185'],
  ['C205', 'C206', 'T206', 'C207', 'C210', 'T210', 'P210'],
  ['C402', 'C404', 'C414', 'C421'],
  ['SR20', 'SR22', 'S22T'],
  ['TBM7', 'TBM8', 'TBM9'],
  ['DA40', 'DA42', 'DA62'],
  ['M20P', 'M20T'],
  ['PC12', 'PC24'],
  ['B06', 'B206', 'B407', 'B505'],
  ['EC45', 'BK17', 'H145'],
  ['EC35', 'H135'],
  ['AS50', 'H125', 'AS55'],
  ['H60', 'UH60', 'S70'],
];

const FAMILY_OF = SAME_FAMILY.reduce((acc, group, i) => {
  group.forEach(code => { acc[code] = i; });
  return acc;
}, {});

/**
 * True when two ICAO designators describe the same airframe closely enough
 * that a photo showing one is NOT evidence the registry's other is wrong.
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export function areSameAircraftFamily(a, b) {
  if (!a || !b) return false;
  const ua = String(a).toUpperCase().trim();
  const ub = String(b).toUpperCase().trim();
  if (ua === ub) return true;
  const fa = FAMILY_OF[ua];
  return fa !== undefined && fa === FAMILY_OF[ub];
}

export default parseAircraftTypeFromPhotoUrl;
